// components/Webinar.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Paper,
  Avatar,
  AvatarGroup,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Stack,
} from "@mui/material";
import {
  PlayArrow,
  CalendarToday,
  AccessTime,
  People,
  Star,
  ArrowForward,
  VideoCall,
  EventNote,
  RocketLaunch,
  Verified,
  TrendingUp,
} from "@mui/icons-material";
import F1 from "../assets/UserPage/Frame1.jpg";
import F2 from "../assets/UserPage/Frame2.jpg";
import "../styles/Section1.css";

const Webinar = () => {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [isHovered, setIsHovered] = useState(false);

  const handleJoinNow = () => {
    const user = localStorage.getItem("user");
    let data = null;
    try {
      data = user ? JSON.parse(user) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
    }

    if (data == null || data.usertype === "") {
      nav("/usersignup");
    } else {
      nav("/webinar");
    }
  };

  const webinarDetails = {
    title: "Join our Amazing Webinar",
    description:
      "Be part of our weekly webinars where amazing lecturers and practitioners take you through topics and courses that would blow your mind.",
    date: "Every Wednesday",
    time: "7:00 PM - 8:30 PM EST",
    platform: "Zoom / Google Meet",
    speakers: 4,
    attendees: 250,
    rating: 4.8,
  };

  const speakers = [
    { name: "Dr. Sarah Johnson", role: "AI Expert", color: "#2BAD65" },
    { name: "Prof. Michael Chen", role: "Data Scientist", color: "#A0EF48" },
    {
      name: "Dr. Emily Rodriguez",
      role: "Cybersecurity Lead",
      color: "#FF6B6B",
    },
    { name: "James Wilson", role: "Full Stack Developer", color: "#FFD93D" },
  ];

  const topics = [
    "Artificial Intelligence & Machine Learning",
    "Cloud Computing & DevOps",
    "Cybersecurity Best Practices",
    "Full Stack Development",
    "Data Science & Analytics",
    "Blockchain Technology",
  ];

  return (
    <Box
      className="webinar-section"
      sx={{
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 3 },
        background: "linear-gradient(180deg, #F7F8F2 0%, #e8efe6 100%)",
        position: "relative",
        overflow: "hidden",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Animated Background Decorations */}
      <Box
        sx={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(43, 173, 101, 0.05) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -200,
          left: -200,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(160, 239, 72, 0.04) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite 2s",
        }}
      />

      {/* Animated Particles */}
      {[...Array(5)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: `${10 + i * 20}%`,
            left: `${5 + (i % 4) * 25}%`,
            width: `${4 + i * 2}px`,
            height: `${4 + i * 2}px`,
            borderRadius: "50%",
            backgroundColor:
              i % 2 === 0
                ? "rgba(43, 173, 101, 0.15)"
                : "rgba(160, 239, 72, 0.15)",
            animation: `float ${6 + i * 2}s ease-in-out infinite ${i * 1.5}s`,
            zIndex: 0,
          }}
        />
      ))}

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={800}>
              <Box>
                {/* Badge */}
                <Chip
                  icon={<EventNote sx={{ fontSize: 16 }} />}
                  label="🎯 Weekly Webinar Series"
                  sx={{
                    backgroundColor: "rgba(43, 173, 101, 0.1)",
                    color: "#2BAD65",
                    fontWeight: 600,
                    mb: 3,
                    px: 2,
                    border: "1px solid rgba(43, 173, 101, 0.15)",
                    "& .MuiChip-icon": {
                      color: "#2BAD65",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(43, 173, 101, 0.15)",
                    },
                    transition: "all 0.3s ease",
                  }}
                />

                {/* Main Heading */}
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "2rem", sm: "2.8rem", md: "3.5rem" },
                    color: "#1a1a1a",
                    mb: 2,
                    lineHeight: 1.2,
                  }}
                >
                  {webinarDetails.title}
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      background:
                        "linear-gradient(135deg, #2BAD65, #A0EF48, #2BAD65)",
                      backgroundSize: "200% 200%",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      animation: "gradientMove 4s ease-in-out infinite",
                      fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                    }}
                  >
                    Every Wednesday! 🎓
                  </Box>
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(26, 26, 26, 0.7)",
                    fontSize: { xs: "1rem", md: "1.05rem" },
                    lineHeight: 1.8,
                    mb: 3,
                    maxWidth: "90%",
                  }}
                >
                  {webinarDetails.description}
                </Typography>

                {/* Webinar Details Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        border: "1px solid rgba(43, 173, 101, 0.08)",
                        borderRadius: 2,
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(43, 173, 101, 0.05)",
                          borderColor: "#2BAD65",
                          transform: "translateY(-3px)",
                        },
                      }}
                    >
                      <CalendarToday
                        sx={{ color: "#2BAD65", fontSize: 24, mb: 0.5 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(26, 26, 26, 0.4)",
                          display: "block",
                          fontSize: "0.6rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Date
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#1a1a1a",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      >
                        Every Wed
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        border: "1px solid rgba(43, 173, 101, 0.08)",
                        borderRadius: 2,
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(160, 239, 72, 0.05)",
                          borderColor: "#A0EF48",
                          transform: "translateY(-3px)",
                        },
                      }}
                    >
                      <AccessTime
                        sx={{ color: "#A0EF48", fontSize: 24, mb: 0.5 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(26, 26, 26, 0.4)",
                          display: "block",
                          fontSize: "0.6rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Time
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#1a1a1a",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      >
                        7:00 PM EST
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        border: "1px solid rgba(43, 173, 101, 0.08)",
                        borderRadius: 2,
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "rgba(255, 107, 107, 0.05)",
                          borderColor: "#FF6B6B",
                          transform: "translateY(-3px)",
                        },
                      }}
                    >
                      <VideoCall
                        sx={{ color: "#FF6B6B", fontSize: 24, mb: 0.5 }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(26, 26, 26, 0.4)",
                          display: "block",
                          fontSize: "0.6rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Platform
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#1a1a1a",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      >
                        Zoom/Google
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Speakers Section */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <AvatarGroup max={4} spacing="small">
                      {speakers.map((speaker, index) => (
                        <Avatar
                          key={index}
                          sx={{
                            width: 34,
                            height: 34,
                            backgroundColor: speaker.color,
                            border: "2px solid #F7F8F2",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            color: index < 2 ? "#ffffff" : "#1a1a1a",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              transform: "scale(1.15)",
                              boxShadow: `0 4px 15px ${speaker.color}40`,
                            },
                          }}
                        >
                          {speaker.name.charAt(0)}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(26, 26, 26, 0.4)" }}
                    >
                      + {webinarDetails.speakers} Expert Speakers
                    </Typography>
                  </Box>
                </Box>

                {/* Stats */}
                <Stack
                  direction="row"
                  spacing={4}
                  sx={{
                    mb: 4,
                    pt: 3,
                    borderTop: "1px solid rgba(43, 173, 101, 0.08)",
                  }}
                >
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        fontSize: { xs: "1.2rem", md: "1.5rem" },
                        background: "linear-gradient(135deg, #2BAD65, #A0EF48)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {webinarDetails.attendees}+
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                    >
                      Monthly Attendees
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        fontSize: { xs: "1.2rem", md: "1.5rem" },
                      }}
                    >
                      {webinarDetails.rating}
                      <Star
                        sx={{
                          fontSize: 20,
                          color: "#FFD93D",
                          verticalAlign: "middle",
                          ml: 0.5,
                        }}
                      />
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                    >
                      Speaker Rating
                    </Typography>
                  </Box>
                </Stack>

                {/* CTA Buttons */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems="center"
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleJoinNow}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    startIcon={<RocketLaunch />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      background: "linear-gradient(135deg, #2BAD65, #A0EF48)",
                      borderRadius: 3,
                      minWidth: 200,
                      color: "#ffffff",
                      boxShadow: "0 4px 25px rgba(43, 173, 101, 0.3)",
                      "&:hover": {
                        transform: "translateY(-3px) scale(1.02)",
                        boxShadow: "0 8px 35px rgba(43, 173, 101, 0.4)",
                        background: "linear-gradient(135deg, #239A56, #8EDB40)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Join Now
                    <ArrowForward
                      sx={{
                        ml: 1,
                        transition: "transform 0.3s ease",
                        transform: isHovered ? "translateX(6px)" : "none",
                      }}
                    />
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<PlayArrow />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#2BAD65",
                      borderColor: "rgba(43, 173, 101, 0.2)",
                      borderRadius: 3,
                      "&:hover": {
                        borderColor: "#2BAD65",
                        backgroundColor: "rgba(43, 173, 101, 0.05)",
                        transform: "translateY(-3px)",
                        boxShadow: "0 8px 30px rgba(43, 173, 101, 0.1)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Watch Previous
                  </Button>
                </Stack>
              </Box>
            </Fade>
          </Grid>

          {/* Right Content - Images */}
          <Grid item xs={12} md={6}>
            <Slide direction="left" in timeout={1000}>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 550,
                    mx: "auto",
                  }}
                >
                  {/* Animated Decorative Elements */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -30,
                      right: -30,
                      width: 140,
                      height: 140,
                      borderRadius: "50%",
                      border: "2px solid rgba(43, 173, 101, 0.1)",
                      zIndex: 0,
                      animation: "spin 20s linear infinite",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -30,
                      left: -30,
                      width: 120,
                      height: 120,
                      borderRadius: "50%",
                      border: "2px solid rgba(160, 239, 72, 0.08)",
                      zIndex: 0,
                      animation: "spin 25s linear infinite reverse",
                    }}
                  />

                  {/* Main Image */}
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow:
                        "0 20px 60px rgba(0, 0, 0, 0.08), 0 0 80px rgba(43, 173, 101, 0.03)",
                      border: "1px solid rgba(43, 173, 101, 0.08)",
                      zIndex: 1,
                      mb: 2,
                    }}
                  >
                    <img
                      src={F1}
                      alt="Webinar"
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        transition:
                          "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                      className="webinar-image"
                    />
                    {/* Live Badge */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 20,
                        left: 20,
                        background: "linear-gradient(135deg, #FF6B6B, #FF8E53)",
                        color: "#ffffff",
                        px: 2,
                        py: 0.6,
                        borderRadius: 50,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        animation: "pulse 2s infinite",
                        boxShadow: "0 4px 20px rgba(255, 107, 107, 0.3)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#ffffff",
                          animation: "pulse 1s infinite",
                        }}
                      />
                      LIVE NOW
                    </Box>

                    {/* Verified Badge */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        right: 20,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        backdropFilter: "blur(10px)",
                        px: 1.5,
                        py: 0.6,
                        borderRadius: 50,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        border: "1px solid rgba(43, 173, 101, 0.15)",
                      }}
                    >
                      <Verified sx={{ color: "#2BAD65", fontSize: 14 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#2BAD65",
                          fontWeight: 600,
                          fontSize: "0.6rem",
                        }}
                      >
                        VERIFIED
                      </Typography>
                    </Box>
                  </Box>

                  {/* Topics Tags - Reduced Gap */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      justifyContent: "center",
                    }}
                  >
                    {topics.slice(0, 3).map((topic, index) => (
                      <Chip
                        key={index}
                        label={topic}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(43, 173, 101, 0.06)",
                          color: "rgba(26, 26, 26, 0.4)",
                          border: "1px solid rgba(43, 173, 101, 0.08)",
                          fontSize: "0.6rem",
                          height: 22,
                          "&:hover": {
                            backgroundColor: "rgba(43, 173, 101, 0.1)",
                            color: "#2BAD65",
                            borderColor: "#2BAD65",
                          },
                        }}
                      />
                    ))}
                    <Chip
                      label="+ More"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(43, 173, 101, 0.1)",
                        color: "#2BAD65",
                        border: "1px solid rgba(43, 173, 101, 0.15)",
                        fontSize: "0.6rem",
                        height: 22,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Slide>
          </Grid>
        </Grid>
      </Container>

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-12px); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.05); }
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes gradientMove {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }

          .webinar-image {
            transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .webinar-image:hover {
            transform: scale(1.05);
          }
        `}
      </style>
    </Box>
  );
};

export default Webinar;
